import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from "lodash";

/**
 * Normalize notification shape at Redux boundary
 * Ensures status is always set and lowercase (handles backend inconsistencies)
 */
function normalizeNotification(n: any): any {
  const rawStatus = n.status;
  const normalizedStatus = typeof rawStatus === 'string'
    ? rawStatus.toLowerCase()
    : 'unread';

  return {
    ...n,
    status: normalizedStatus,
  };
}

interface NotificationPayload {
  message: string;
}

interface NotificationState {
  notification: NotificationPayload[];
  notificationPath: number[];
  notificationCount: number;
  companyNotificationCount: number;
  candidateNotificationCount: number;
}

const initialState: NotificationState = {
  notification: [],
  notificationPath: [],
  notificationCount: 0,
  companyNotificationCount: 0,
  candidateNotificationCount: 0,
};

export const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<any>) => {
      // Normalize incoming notification (socket or sync) – list state only
      const normalized = normalizeNotification(action.payload);
      // Prevent duplicates by checking if notification already exists
      const exists = state.notification.some(
        (n: any) => n._id === normalized._id
      );
      if (!exists) {
        state.notification = [normalized, ...state.notification];
      }
    },
    updateNotificationList: (
      state,
      action: PayloadAction<NotificationPayload[]>
    ) => {
      // Normalize all notifications on fetch/refresh
      state.notification = action.payload.map(normalizeNotification);
    },
    // Canonical way to update the unread badge count from the backend.
    // Badge/count logic should NEVER be mutated via local math or socket deltas or inferred from sockets.
    setUnreadCountFromDB: (state, action: PayloadAction<number>) => {
      state.notificationCount = action.payload;
    },
    setCompanyUnreadCountFromDB: (state, action: PayloadAction<number>) => {
      state.companyNotificationCount = action.payload;
    },
    setCandidateUnreadCountFromDB: (state, action: PayloadAction<number>) => {
      state.candidateNotificationCount = action.payload;
    },
    // Fast-path optimistic increment for real-time socket events.
    // Will be reconciled by setUnreadCountFromDB on next DB fetch
    // (panel open, reconnect, tab-visible).
    incrementUnreadCount: (state) => {
      state.notificationCount += 1;
    },
    incrementCompanyUnreadCount: (state) => {
      state.companyNotificationCount += 1;
    },
    incrementCandidateUnreadCount: (state) => {
      state.candidateNotificationCount += 1;
    },
    setNotificationPath: (state, action: PayloadAction<number[]>) => {
      state.notificationPath = _.union(state.notificationPath, action.payload);
    },
    updateNotificationPath: (state, action: PayloadAction<number>) => {
      state.notificationCount = action.payload;
    },
    pushNotificationPath: (state, action: PayloadAction<number>) => {
      state.notificationPath = [...state.notificationPath, action.payload];
    },
    removeNotificationPath: (state, action: PayloadAction<number>) => {
      state.notificationPath = state.notificationPath.filter((item) => {
        return item !== action.payload;
      });
    },
    // Mark all notifications as read (preserves notifications, just updates status)
    // This should be used for "Mark all as read" button
    markAllAsRead: (state) => {
      state.notification = state.notification.map((n: any) => ({
        ...n,
        status: 'read'
      }));
    },
    // Mark a single notification as read
    markOneAsRead: (state, action: PayloadAction<string>) => {
      state.notification = state.notification.map((n: any) =>
        n._id === action.payload ? { ...n, status: 'read' } : n
      );
    },
    // Delete notifications from local state (for after API delete calls)
    removeNotifications: (state, action: PayloadAction<string[]>) => {
      const idsToRemove = action.payload;
      state.notification = state.notification.filter(
        (n: any) => !idsToRemove.includes(n._id)
      );
    },
    // Clear all notifications (only for explicit "Clear All" delete action)
    clearNotifications: (state) => {
      state.notification = [];
      state.notificationCount = 0;
      state.companyNotificationCount = 0;
      state.candidateNotificationCount = 0;
    },
  },
});

export const {
  addNotification,
  updateNotificationList,
  setUnreadCountFromDB,
  setCompanyUnreadCountFromDB,
  setCandidateUnreadCountFromDB,
  incrementUnreadCount,
  incrementCompanyUnreadCount,
  incrementCandidateUnreadCount,
  setNotificationPath,
  updateNotificationPath,
  pushNotificationPath,
  removeNotificationPath,
  markAllAsRead,
  markOneAsRead,
  removeNotifications,
  clearNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
